import { useHirerAuth } from '@seek/hirer-auth-react';
import { useTranslations } from '@vocab/react';
import { Box, ContentBlock, Heading, Stack } from 'braid-design-system';
import React, { useEffect, useState } from 'react';

import { useTracking } from 'src/App/context/TrackingProvider.tsx';

import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary';
import { ErrorSectionWithLayout } from '../../components/ErrorSection/ErrorSectionWithLayout';
import LargerBudgetPurchaseAlert from '../../components/LargerBudgetPurchaseAlert/LargerBudgetPurchaseAlert';
import { SystemBanner } from '../../components/SystemBanner/SystemBanner';
import { useAppContext } from '../../context/AppContext';
import {
  type CreateProductSetForBudgetsPurchaseForHirerMutation,
  type CreateProductSetForBudgetsPurchaseForVisitorMutation,
  useCreateProductSetForBudgetsPurchaseForHirerMutation,
  useCreateProductSetForBudgetsPurchaseForVisitorMutation,
  useHirerAccountQuery,
} from '../../generated/graphql-types';
import { useQueryAccountInfo } from '../../hooks/useQueryAccountInfo.ts';
import { AppError } from '../../utils/error';
import { getLogger } from '../../utils/logger/logger';

import translations from './.vocab';
import { AdBudgetValueProposition } from './components/AdBudgetValueProposition/AdBudgetValueProposition';
import { BudgetOptions } from './components/BudgetOptions/BudgetOptions';
import { CreditHoldAlert } from './components/CreditHoldAlert/CreditHoldAlert';
import { Faq } from './components/Faq/Faq';
import { LoadingBlock } from './components/Loading/Loading.tsx';
import { PriceCheckerPanel } from './components/PriceCheckerPanel/PriceCheckerPanel';
import { toError } from './error.ts';
import { createAdBudgetSelectionPageBaseEvent } from './eventTracking/createEvent';
import {
  type AdBudgetProductItemType,
  type BudgetOption,
  mapBudgetOption,
} from './mapBudgetOption';

import * as styles from './AdBudgetsPage.css';

interface Props {
  uuid: string;
}

type BudgetsProductSet =
  | CreateProductSetForBudgetsPurchaseForVisitorMutation['createProductSetForBudgetsPurchaseForVisitor']['productSet']
  | CreateProductSetForBudgetsPurchaseForHirerMutation['createProductSetForBudgetsPurchaseForHirer']['productSet'];

const prepareBudgetsForDisplay = (
  productSet: BudgetsProductSet,
  country: string,
) =>
  productSet.items
    .map((item) => mapBudgetOption(item as AdBudgetProductItemType, country))
    .sort(
      (budgetOptionA, budgetOptionB) =>
        budgetOptionA.budgetValue - budgetOptionB.budgetValue,
    );

export const AdBudgetsPage = ({ uuid }: Props) => {
  const { country, language } = useAppContext();
  const [selectedBudgetType, setSelectedBudgetType] = useState('BrandedBudget');
  const [budgetOptions, setBudgetOptions] = useState<BudgetOption[]>([]);
  const [productSetId, setProductSetId] = useState('');
  const [initialised, setIsInitialised] = useState(false);
  const { trackView, trackLink } = useTracking();
  const [, setErrorState] = useState();
  const { t } = useTranslations(translations);
  const { authenticated, loading: authLoading } = useHirerAuth();
  const { accountInfo, loading: accountLoading } = useQueryAccountInfo();
  const advertiserId = accountInfo?.currentAdvertiser?.advertiserId ?? '';

  const {
    data: hirerAccountData,
    loading: hirerAccountDataLoading,
    error: hirerAccountError,
  } = useHirerAccountQuery({
    skip: !advertiserId,
  });

  const [
    createProductSetForBudgetsPurchaseForVisitor,
    { data: visitorData, loading: visitorLoading },
  ] = useCreateProductSetForBudgetsPurchaseForVisitorMutation({
    onError: (error) => {
      // https://www.developerway.com/posts/how-to-handle-errors-in-react
      setErrorState(() => {
        throw toError(error);
      });
    },
  });

  const [
    createProductSetForBudgetsPurchaseForHirer,
    { data: hirerData, loading: hirerLoading },
  ] = useCreateProductSetForBudgetsPurchaseForHirerMutation({
    onError: (error) => {
      // https://www.developerway.com/posts/how-to-handle-errors-in-react
      setErrorState(() => {
        throw toError(error);
      });
    },
  });

  const hirerBillingStatus = hirerAccountData?.hirerAccount?.billingStatus;
  const isOnCreditHold = hirerBillingStatus === 'ON_CREDIT_HOLD';

  useEffect(() => {
    if (authLoading || hirerAccountDataLoading || accountLoading) {
      return;
    }

    if (authenticated && !isOnCreditHold) {
      createProductSetForBudgetsPurchaseForHirer({
        variables: { input: { opportunityId: uuid } },
      });
    } else {
      createProductSetForBudgetsPurchaseForVisitor({
        variables: { input: { visitorId: uuid } },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authLoading,
    accountLoading,
    authenticated,
    hirerAccountDataLoading,
    isOnCreditHold,
  ]);

  if (hirerAccountError) {
    const accountError = new AppError(
      'HIRER_ACCOUNT_QUERY_ERROR',
      'Error retrieving hirer account details',
      hirerAccountError,
    );
    getLogger().error(accountError.message, {
      activity: 'initialiseAdBudgetsInformation',
      error: accountError,
    });
  }

  const loading =
    hirerLoading ||
    visitorLoading ||
    hirerAccountDataLoading ||
    authLoading ||
    accountLoading ||
    (!visitorData && !hirerData);

  // TODO: ELLIOTT - AT-6841 - refactor this business logic into a HOOK. This is a temporary solution to avoid stuff. :D
  useEffect(() => {
    if (loading) {
      return;
    }

    if (visitorData) {
      const loadedBudgetOptions = prepareBudgetsForDisplay(
        visitorData.createProductSetForBudgetsPurchaseForVisitor.productSet,
        country,
      );

      setBudgetOptions(loadedBudgetOptions);
      setProductSetId(
        visitorData.createProductSetForBudgetsPurchaseForVisitor.productSet.id,
      );
      setSelectedBudgetType(
        loadedBudgetOptions.find(
          (budget) => budget.budgetType === 'PremiumBudget',
        )
          ? 'PremiumBudget'
          : 'BrandedBudget',
      );
      setIsInitialised(true);
    } else if (hirerData) {
      const loadedBudgetOptions = prepareBudgetsForDisplay(
        hirerData.createProductSetForBudgetsPurchaseForHirer.productSet,
        country,
      );
      setBudgetOptions(loadedBudgetOptions);
      setProductSetId(
        hirerData.createProductSetForBudgetsPurchaseForHirer.productSet.id,
      );
      setSelectedBudgetType(
        loadedBudgetOptions.find(
          (budget) => budget.budgetType === 'PremiumBudget',
        )
          ? 'PremiumBudget'
          : 'BrandedBudget',
      );
      setIsInitialised(true);
    }

    // Once agian, we "should" reference all the dependencies, but we're being lazy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, visitorData, hirerData]);

  const trackingBaseEvent = createAdBudgetSelectionPageBaseEvent({
    adBudgetType: selectedBudgetType,
    adBudgetsList: budgetOptions,
    ...(hirerAccountData &&
      hirerAccountData.hirerAccount && {
        pricingModel: hirerAccountData.hirerAccount.pricingModel,
      }),
    productSetId,
    currentPage: 'seek contracts',
  });

  useEffect(() => {
    if (!initialised) {
      return;
    }

    trackView({
      eventName: 'seek_contract_displayed',
      data: {
        ...trackingBaseEvent,
      },
    });
    // We only want to trigger based on initialised. Yes... we should have all our dependencies in the array, but we're being lazy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialised]);

  const priceCheckerOnClick = (eventName: string) => {
    // If we haven't loaded our budget options, we shouldn't be tracking anything.
    if (!initialised) {
      return;
    }

    trackLink({
      eventName,
      data: trackingBaseEvent,
    });
  };

  return (
    <ErrorBoundary errorDisplay={<ErrorSectionWithLayout />}>
      <Box background="surface">
        <SystemBanner context="ad_budget_product_page" />
        <ContentBlock>
          <Box paddingTop={['xlarge', 'xxlarge']}>
            <Stack space="xlarge" align="center">
              <Box
                marginX={{ tablet: 'gutter', mobile: 'gutter' }}
                className={styles.creditHoldAlert}
              >
                {hirerBillingStatus && isOnCreditHold && <CreditHoldAlert />}
              </Box>
              <Stack space="large" align="center">
                <Box paddingX="medium" paddingBottom="medium">
                  <Stack space="xlarge" align="center">
                    <Heading align="center" level="1">
                      {t('Pay upfront and get a discount...')}
                    </Heading>
                    <Box paddingX="medium" maxWidth="small">
                      <Heading level="3" weight="weak" align="center">
                        {t('Choose an Ad Budget and use...')}
                      </Heading>
                    </Box>
                  </Stack>
                </Box>

                <Box
                  marginX={{ mobile: 'gutter' }}
                  className={styles.alertAndPriceCheckerPanel}
                >
                  <Stack space="gutter" align="center">
                    {loading ||
                    selectedBudgetType === '' ||
                    budgetOptions.length === 0 ? (
                      <LoadingBlock />
                    ) : (
                      <BudgetOptions
                        budgetOptions={budgetOptions}
                        selectedBudgetType={selectedBudgetType}
                        setSelectedBudgetType={setSelectedBudgetType}
                        trackingBaseEvent={trackingBaseEvent}
                        productSetId={productSetId}
                        showBuyNowButton={!isOnCreditHold}
                      />
                    )}
                    {!isOnCreditHold && <LargerBudgetPurchaseAlert />}
                    <PriceCheckerPanel
                      country={country}
                      language={language}
                      onClick={priceCheckerOnClick}
                    />
                  </Stack>
                </Box>
              </Stack>
              <AdBudgetValueProposition
                selectedBudgetType={selectedBudgetType}
              />
              <Faq
                selectedBudgetType={selectedBudgetType}
                trackingBaseEvent={trackingBaseEvent}
              />
            </Stack>
          </Box>
        </ContentBlock>
      </Box>
    </ErrorBoundary>
  );
};
